import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, CalendarDays, Clock, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Discovery-call calendar picker.
 * - Loads 14 days of availability from the backend (same for both pilots).
 * - Horizontally scrollable date strip, then a 3-column time-slot grid.
 * - Emits the selected slot ISO (UTC) via `onSelect`.
 */
export default function CalendarPicker({ selectedSlot, onSelect }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeDateIdx, setActiveDateIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const r = await axios.get(`${API}/discovery-bookings/availability?days=14`);
        if (cancelled) return;
        setData(r.data);
        // Auto-select first day with any availability
        const firstOpen = r.data.days.findIndex((d) => d.hasAvailability);
        setActiveDateIdx(firstOpen >= 0 ? firstOpen : 0);
      } catch (e) {
        if (!cancelled)
          setError("We couldn't load available times. Please refresh and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeDay = useMemo(
    () => (data?.days?.[activeDateIdx] ?? null),
    [data, activeDateIdx]
  );

  if (loading) {
    return (
      <div
        data-testid="calendar-loading"
        className="rounded-2xl bg-soft border border-border-neutral p-10 flex items-center justify-center text-mid-neutral"
      >
        <Loader2 className="h-5 w-5 animate-spin mr-3" />
        Loading available times…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        role="alert"
        data-testid="calendar-error"
        className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
      >
        {error || "No availability could be loaded."}
      </div>
    );
  }

  const days = data.days || [];

  return (
    <div data-testid="calendar-picker" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-forest text-lime shrink-0">
          <CalendarDays className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-extrabold text-forest">Pick a time that suits</p>
          <p className="text-xs text-mid-neutral">
            30-minute Discovery Call · shown in Sydney time (AEST/AEDT) ·
            earliest slot is 24 hours from now
          </p>
        </div>
      </div>

      {/* Date strip */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveDateIdx((i) => Math.max(0, i - 1))}
            disabled={activeDateIdx === 0}
            data-testid="calendar-date-prev"
            aria-label="Previous day"
            className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full border border-border-neutral text-forest hover:bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 flex overflow-x-auto gap-2 pb-1 scroll-smooth snap-x">
            {days.map((d, i) => {
              const active = i === activeDateIdx;
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => setActiveDateIdx(i)}
                  data-testid={`calendar-date-${d.date}`}
                  className={
                    "snap-start shrink-0 rounded-2xl border-2 px-4 py-3 min-w-[92px] text-center transition-all " +
                    (active
                      ? "border-forest bg-forest text-white"
                      : d.hasAvailability
                      ? "border-border-neutral bg-white text-forest hover:border-mint"
                      : "border-border-neutral bg-soft text-mid-neutral/60 hover:border-border-neutral")
                  }
                >
                  <div className="text-[10px] uppercase tracking-widest font-bold">
                    {d.weekday}
                  </div>
                  <div className="text-lg font-extrabold mt-0.5">
                    {d.dayLabel.split(" ")[1] /* day number */}
                  </div>
                  <div
                    className={
                      "text-[10px] mt-0.5 " +
                      (active
                        ? "text-lime"
                        : d.hasAvailability
                        ? "text-mint"
                        : "text-mid-neutral/60")
                    }
                  >
                    {d.hasAvailability ? "open" : "full"}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setActiveDateIdx((i) => Math.min(days.length - 1, i + 1))}
            disabled={activeDateIdx >= days.length - 1}
            data-testid="calendar-date-next"
            aria-label="Next day"
            className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full border border-border-neutral text-forest hover:bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Slot grid */}
      {activeDay && (
        <div data-testid="calendar-slots" className="pt-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-forest mb-3">
            <Clock className="h-4 w-4 text-mint" />
            {activeDay.dayLabel}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {activeDay.slots.map((s) => {
              const isSelected = selectedSlot === s.iso;
              return (
                <button
                  key={s.iso}
                  type="button"
                  disabled={!s.available}
                  onClick={() => onSelect(s.iso, {
                    label: `${activeDay.dayLabel}, ${s.label}`,
                    time: s.label,
                    date: activeDay.dayLabel,
                  })}
                  data-testid={`calendar-slot-${s.iso}`}
                  className={
                    "rounded-xl border-2 py-3 text-sm font-bold transition-all " +
                    (!s.available
                      ? "border-border-neutral bg-soft text-mid-neutral/50 line-through cursor-not-allowed"
                      : isSelected
                      ? "border-forest bg-forest text-white"
                      : "border-border-neutral bg-white text-forest hover:border-mint")
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          {!activeDay.hasAvailability && (
            <p className="mt-4 text-xs text-mid-neutral">
              No open times this day — please try the next available day.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
